/**
 * The platform's binding of the shared Support module — `SupportRuntime`
 * (`@twasta/page-runtime/support`) over `/api/support/{project}/...`
 * (`routers/support.py`), plus the builder-side notification bell over
 * `/api/platform-notifications`.
 *
 * Every call goes through the shared axios client, so a PROJECT USER's Run
 * session headers and the release-management environment headers
 * (`api/appEnv`) ride along without this module knowing about either.
 */

import api from './client'
import { APP_BASE } from '@/config/basePath'
import type {
  AgentTranscript, DeploymentPatch, DeploymentsPage, DiffResult, IntakeKey, IssueAttachment,
  IssueDetailData as IssueDetail, IssueListPage, IssueListQuery, IssuePatch, LiveDeployment,
  MintedIntakeKey, NewIssueInput, NewReleaseInput, PolicyResponse, QcInput, Release, ReleasePatch,
  ReleasesPage, RetryStage, StartVersioningInput, StartVersioningResult, SupportCounts,
  SupportEnvironment, SupportMeta, SupportPolicyDoc, SupportRuntime, VersioningStatus,
} from '@twasta/page-runtime/support'
import type { AppNotification, NotificationRuntime } from '@twasta/page-runtime/shell'

const enc = encodeURIComponent

/** A `SupportRuntime` for one application, as the platform serves it. */
export function makeSupportRuntime(project: string): SupportRuntime {
  const base = `/support/${enc(project)}`
  const issue = (n: number) => `${base}/issues/${enc(String(n))}`
  return {
    // ── reporter side ──
    meta: async () => (await api.get<SupportMeta>(`${base}/meta`)).data,
    counts: async () => (await api.get<SupportCounts>(`${base}/counts`)).data,
    listIssues: async (q: IssueListQuery = {}) => {
      const params: Record<string, string | number | boolean> = {}
      if (q.scope) params.scope = q.scope
      if (q.status?.length) params.status = q.status.join(',')
      if (q.type) params.type = q.type
      if (q.severity) params.severity = q.severity
      if (q.tenant) params.tenant = q.tenant
      if (q.assignee) params.assignee = q.assignee
      if (q.q) params.q = q.q
      if (q.open) params.open = true
      if (q.limit) params.limit = q.limit
      if (q.offset) params.offset = q.offset
      return (await api.get<IssueListPage>(`${base}/issues`, { params })).data
    },
    getIssue: async (n) => (await api.get<IssueDetail>(issue(n))).data,
    createIssue: async (input: NewIssueInput) => (await api.post<IssueDetail>(`${base}/issues`, input)).data,
    transition: async (n, event, comment) =>
      (await api.post<IssueDetail>(`${issue(n)}/transition`, { event, comment: comment || null })).data,
    comment: async (n, body, opts) =>
      (await api.post<IssueDetail>(`${issue(n)}/comments`, { body, internal: !!opts?.internal })).data,
    watch: async (n, on) =>
      (await api.post<{ watching: boolean }>(`${issue(n)}/watch`, null, { params: { on } })).data,
    uploadAttachment: async (n, file, filename) => {
      const form = new FormData()
      form.append('file', file, filename || (file as File).name || 'file')
      // The client's JSON default must not reach a multipart body: let the
      // browser write the boundary.
      return (await api.post<IssueAttachment>(`${issue(n)}/attachments`, form, {
        headers: { 'Content-Type': undefined as unknown as string },
      })).data
    },
    // Fetched, not linked: a project user's session is a bearer token, which
    // a plain <a href> cannot carry.
    downloadAttachment: async (n, attId) =>
      (await api.get<Blob>(`${issue(n)}/attachments/${enc(String(attId))}`, { responseType: 'blob' })).data,
    attachmentUrl: (n, attId) => `${APP_BASE}/api${issue(n)}/attachments/${enc(String(attId))}`,
    environment: async (env?: string) =>
      (await api.get<SupportEnvironment>(`${base}/environment`, { params: env ? { env } : {} })).data,

    // ── team side ──
    patchIssue: async (n, patch: IssuePatch) =>
      (await api.patch<{ changed: string[]; issue: IssueDetail }>(issue(n), patch)).data,
    qc: async (n, input: QcInput) => (await api.post<IssueDetail>(`${issue(n)}/qc`, input)).data,
    diff: async (n, path) =>
      (await api.get<DiffResult>(`${issue(n)}/diff`, { params: path ? { path } : {} })).data,
    retry: async (n, stage: RetryStage) =>
      (await api.post<{ queued: number }>(`${issue(n)}/retry`, { stage })).data,
    transcript: async (runId) =>
      (await api.get<AgentTranscript>(`${base}/agent-runs/${enc(String(runId))}/transcript`)).data,
    getPolicy: async () => (await api.get<PolicyResponse>(`${base}/policy`)).data,
    putPolicy: async (patch: SupportPolicyDoc) =>
      (await api.put<{ policy: SupportPolicyDoc }>(`${base}/policy`, patch)).data,

    // ── releases ──
    listReleases: async () => (await api.get<ReleasesPage>(`${base}/releases`)).data,
    createRelease: async (input: NewReleaseInput) => (await api.post<Release>(`${base}/releases`, input)).data,
    getRelease: async (id) => (await api.get<Release>(`${base}/releases/${enc(String(id))}`)).data,
    patchRelease: async (id, patch: ReleasePatch) =>
      (await api.patch<Release>(`${base}/releases/${enc(String(id))}`, patch)).data,
    cutRelease: async (id) => (await api.post<Release>(`${base}/releases/${enc(String(id))}/cut`)).data,
    discardRelease: async (id) =>
      (await api.delete<{ discarded: boolean }>(`${base}/releases/${enc(String(id))}`)).data,

    // ── versioning + intake keys ──
    versioningStatus: async () => (await api.get<VersioningStatus>(`${base}/versioning`)).data,
    listDeployments: async () => (await api.get<DeploymentsPage>(`${base}/deployments`)).data,
    configureDeployment: async (id: string, patch: DeploymentPatch) =>
      (await api.put<LiveDeployment>(`${base}/deployments/${enc(id)}`, patch)).data,
    startVersioning: async (input: StartVersioningInput) =>
      (await api.post<StartVersioningResult>(`${base}/versioning/start`, input)).data,
    listIntakeKeys: async () => (await api.get<{ keys: IntakeKey[] }>(`${base}/intake-keys`)).data,
    mintIntakeKey: async (label: string) =>
      (await api.post<MintedIntakeKey>(`${base}/intake-keys`, { label })).data,
    revokeIntakeKey: async (id: string) =>
      (await api.delete<{ revoked: boolean }>(`${base}/intake-keys/${enc(id)}`)).data,

    // No updates/preflight/upgrade/rollback here: the platform is the
    // development and test environment, and each LIVE server upgrades itself
    // from the release's git tag. `UpdatesPanel` belongs to the downloaded
    // runtime client, which upgrades its own server.
  }
}

// ── builder-side notifications (the platform top bar's bell) ───────────────

interface PlatformNotificationRow {
  id: number
  kind: string
  title: string
  body?: string | null
  link?: Record<string, unknown> | null
  read: boolean
  created_at?: string | null
}

function toAppNotification(r: PlatformNotificationRow): AppNotification {
  return {
    notif_id: String(r.id),
    kind: r.kind,
    title: r.title,
    body: r.body ?? null,
    link: r.link && typeof r.link === 'object' ? r.link : null,
    created_at: r.created_at ?? null,
    read: !!r.read,
  }
}

/**
 * The shared `NotificationBell`'s transport for a Twasta account's OWN
 * notifications — what the Support pipeline tells the people building the
 * application (a plan to approve, a build that failed, a release out). No
 * mute preferences: the platform store has none, so the bell hides that
 * control rather than offering one that cannot save.
 */
export const platformNotificationRuntime: NotificationRuntime = {
  list: async (opts) => {
    const { data } = await api.get<{ notifications: PlatformNotificationRow[]; unread: number }>(
      '/platform-notifications',
      { params: { unread: !!opts?.unreadOnly, limit: opts?.limit || 50 } },
    )
    return { items: (data.notifications || []).map(toAppNotification), unread: data.unread || 0 }
  },
  count: async () => (await api.get<{ unread: number }>('/platform-notifications/count')).data,
  markRead: async (ids?: string[]) => {
    const body = ids && ids.length
      ? { ids: ids.map((i) => Number(i)).filter((i) => Number.isFinite(i)) }
      : { ids: null }
    return (await api.post<{ marked: number }>('/platform-notifications/read', body)).data
  },
}
